import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Crown } from "lucide-react";

interface UserAvatarProps {
  userId?: string;
  avatarUrl?: string | null;
  fallback?: string;
  size?: "sm" | "md" | "lg" | "xl";
  showLevel?: boolean;
  className?: string;
  clickable?: boolean;
  onClick?: () => void;
}

const sizeClasses = {
  sm: "h-8 w-8",
  md: "h-10 w-10", 
  lg: "h-16 w-16",
  xl: "h-24 w-24"
};

const sizePixels = {
  sm: 32,
  md: 40,
  lg: 64,
  xl: 96
};

const levelBadgeSize = {
  sm: "h-5 w-5 text-[10px]",
  md: "h-6 w-6 text-xs",
  lg: "h-7 w-7 text-sm", 
  xl: "h-9 w-9 text-base"
};

export const UserAvatar = ({ 
  userId, 
  avatarUrl, 
  fallback = "?", 
  size = "md", 
  showLevel = true,
  className,
  clickable = true,
  onClick
}: UserAvatarProps) => {
  const navigate = useNavigate();
  const { data: userLevel } = useQuery({
    queryKey: ['user-level', userId],
    queryFn: async () => {
      if (!userId) return null;
      
      console.log("🔍 UserAvatar: Fetching level for user:", userId);
      
      const { data, error } = await supabase
        .from('user_xp')
        .select('level')
        .eq('user_id', userId)
        .maybeSingle();
        
      if (error) {
        console.log("❌ UserAvatar: Error fetching level:", error);
        return null;
      }
      
      const level = data?.level ?? null;
      console.log("✅ UserAvatar: Level for user", userId, "is", level);
      return level;
    },
    enabled: !!userId && showLevel
  });

  // Check if user is Head Provider using the new public function
  const { data: isHeadProvider } = useQuery({
    queryKey: ['user-admin-status', userId],
    queryFn: async () => {
      if (!userId) return false;
      
      console.log("🔍 UserAvatar: Checking admin status for user:", userId);
      
      const { data, error } = await supabase.rpc('is_user_admin', {
        target_user_id: userId
      });
        
      if (error) {
        console.log("❌ UserAvatar: Error checking admin status:", error);
        return false;
      }
      
      console.log("✅ UserAvatar: Admin status for user", userId, "is", data);
      return Boolean(data);
    },
    enabled: !!userId
  });

  
  console.log("🎭 UserAvatar render - userId:", userId, "userLevel:", userLevel, "showLevel:", showLevel, "isHeadProvider:", isHeadProvider);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (clickable && userId && userId.trim() && userId !== 'undefined' && userId !== 'null') {
      navigate(`/profile/${userId}`);
    }
  };

  const isClickable = clickable && (userId || onClick);

  // Use clean avatar URL without transformations (transformations not enabled in storage)
  const cleanAvatarUrl = avatarUrl ? avatarUrl.split('?')[0] : avatarUrl;
  const pixelSize = sizePixels[size];

  return (
    <div 
      className={cn(
        "relative inline-block",
        isClickable && "cursor-pointer hover:opacity-80 transition-opacity"
      )}
      onClick={isClickable ? handleClick : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      } : undefined}
    >
      <Avatar className={cn(sizeClasses[size], className)}>
        {cleanAvatarUrl && (
          <AvatarImage 
            src={cleanAvatarUrl} 
            alt="User avatar"
            loading="lazy"
            style={{ 
              width: `${pixelSize}px`, 
              height: `${pixelSize}px`,
              objectFit: 'cover'
            }}
          />
        )}
        <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/30 text-primary font-medium border border-primary/20">
          {fallback}
        </AvatarFallback>
      </Avatar>
      
      {showLevel && userLevel !== null && userLevel !== undefined && (
        <div 
          className={cn(
            "absolute -bottom-1 -right-1 bg-blue-500 text-white font-bold flex items-center justify-center rounded-full min-w-fit px-1",
            levelBadgeSize[size]
          )}
        >
          {userLevel}
        </div>
      )}
      
      {isHeadProvider && (
        <div 
          className={cn(
            "absolute -top-1 -right-1 bg-amber-400 text-amber-900 flex items-center justify-center rounded-full p-1 shadow-lg border border-amber-500",
            {
              "w-5 h-5": size === "sm",
              "w-6 h-6": size === "md", 
              "w-7 h-7": size === "lg",
              "w-8 h-8": size === "xl"
            }
          )}
        >
          <Crown className={cn("fill-current", {
            "w-3 h-3": size === "sm",
            "w-4 h-4": size === "md",
            "w-5 h-5": size === "lg", 
            "w-6 h-6": size === "xl"
          })} />
        </div>
      )}
    </div>
  );
};